import { Select, SelectItem, type SharedSelection, cn } from "@heroui/react";
import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { MonthRangeButton } from "./month-range-picker__button";
import type { Month } from "./types";

// Calendar data - using 0-based month indexes (January = 0)
const MONTHS: Month[][] = [
    [
        { number: 0, name: "Jan", yearOffset: 0 },
        { number: 1, name: "Feb", yearOffset: 0 },
        { number: 2, name: "Mär", yearOffset: 0 },
        { number: 3, name: "Apr", yearOffset: 0 },
    ],
    [
        { number: 4, name: "Mai", yearOffset: 0 },
        { number: 5, name: "Jun", yearOffset: 0 },
        { number: 6, name: "Jul", yearOffset: 0 },
        { number: 7, name: "Aug", yearOffset: 0 },
    ],
    [
        { number: 8, name: "Sep", yearOffset: 0 },
        { number: 9, name: "Okt", yearOffset: 0 },
        { number: 10, name: "Nov", yearOffset: 0 },
        { number: 11, name: "Dez", yearOffset: 0 },
    ],
];

// Helper function to create a timezone-safe date
const createSafeDate = (year: number, month: number): Date => {
    // Use UTC to avoid timezone issues
    return new Date(Date.UTC(year, month, 1, 12, 0, 0));
};

export type MonthRangeCalendarProps = {
    selectedMonth?: Date;
    onMonthSelect?: (date: Date, closePopover?: boolean) => void;
    minDate?: Date;
    maxDate?: Date;
    showQuickSelectors?: boolean;
    isDisabled?: boolean;
    centerYearSelect?: boolean;
    yearSelectClassName?: string;
    withNav?: boolean;
};

const MonthRangeCalendar = ({
    selectedMonth,
    onMonthSelect,
    minDate = new Date(0),
    maxDate,
    isDisabled,
    centerYearSelect = true,
    yearSelectClassName,
    withNav,
}: MonthRangeCalendarProps) => {
    // State management - ensure we track the year from the selected month
    const [menuYear, setMenuYear] = useState<number>(
        selectedMonth?.getFullYear() ?? new Date().getFullYear(),
    );
    const startYear = minDate.getFullYear();
    const endYear = maxDate ? maxDate.getFullYear() : new Date().getFullYear();

    const years = useMemo(() => {
        return Array.from(
            { length: endYear - startYear + 1 },
            (_, i) => startYear + i,
        )
            .map((year) => ({
                key: year.toString(),
            }))
            .reverse();
    }, [startYear, endYear]);

    // Update menuYear when selectedMonth changes externally
    useEffect(() => {
        if (selectedMonth) {
            setMenuYear(selectedMonth.getFullYear());
        }
    }, [selectedMonth]);

    // Validate min/max dates
    if (minDate && maxDate && minDate > maxDate) {
        minDate = maxDate;
    }

    const handleMonthClick = useCallback(
        (month: Month) => {
            if (onMonthSelect) {
                // Create a timezone-safe date
                const actualYear = menuYear + month.yearOffset;
                const exactDate = createSafeDate(actualYear, month.number);
                // Call the selection handler with the constructed date
                onMonthSelect(exactDate);
            }
        },
        [menuYear, onMonthSelect],
    );

    const onYearChange = useCallback(
        (selectedYear: number) => {
            const exactDate = createSafeDate(
                selectedYear,
                selectedMonth.getMonth(),
            );

            setMenuYear(selectedYear);
            onMonthSelect(exactDate, false);
        },
        [onMonthSelect, selectedMonth],
    );

    const handleYearSelect = useCallback(
        (selection: SharedSelection) => {
            if (!selection.currentKey) {
                return;
            }

            const selectedYear = Number.parseInt(selection.currentKey, 10);
            onYearChange(selectedYear);
        },
        [onYearChange],
    );

    return (
        <section
            className={cn(
                "flex flex-col gap-4",
                isDisabled && "pointer-events-none opacity-50",
            )}
        >
            <div className="w-full min-w-[330px] space-y-4 md:min-w-[400px]">
                <header className="relative flex items-center justify-center pt-1">
                    <h4 className="font-medium text-sm">
                        <Select
                            className={cn("w-32", yearSelectClassName)}
                            items={years}
                            placeholder="Select an animal"
                            size="sm"
                            selectedKeys={[menuYear.toString()]}
                            onSelectionChange={handleYearSelect}
                            disallowEmptySelection={true}
                            classNames={
                                centerYearSelect
                                    ? {
                                          trigger: "justify-center",
                                          value: "text-center",
                                      }
                                    : {
                                          trigger: "pl-4",
                                      }
                            }
                            renderValue={(items) =>
                                items.map((item) => item.key)
                            }
                        >
                            {(year) => (
                                <SelectItem>
                                    <div
                                        className={cn(
                                            "ml-0",
                                            centerYearSelect && "ml-8",
                                        )}
                                    >
                                        {year.key}
                                    </div>
                                </SelectItem>
                            )}
                        </Select>
                    </h4>
                    {withNav && (
                        <nav className="flex items-center space-x-1">
                            <button
                                type="button"
                                onClick={() => onYearChange(menuYear - 1)}
                                className={cn(
                                    "absolute left-1 inline-flex h-7 w-7 items-center justify-center p-0",
                                    menuYear <= startYear && "opacity-50",
                                )}
                                disabled={menuYear <= startYear}
                            >
                                <IconChevronLeft className="h-4 w-4 opacity-50" />
                            </button>
                            <button
                                type="button"
                                onClick={() => onYearChange(menuYear + 1)}
                                className={cn(
                                    "absolute right-1 inline-flex h-7 w-7 items-center justify-center p-0",
                                    menuYear >= endYear && "opacity-50",
                                )}
                                disabled={menuYear >= endYear}
                            >
                                <IconChevronRight className="h-4 w-4 opacity-50" />
                            </button>
                        </nav>
                    )}
                </header>
                <div className="overflow-x-auto">
                    <table className="w-full border-collapse space-y-1">
                        <tbody>
                            {MONTHS.map((monthRow) => (
                                <tr
                                    key={monthRow[0].number}
                                    className="mt-2 flex w-full"
                                >
                                    {monthRow.map((month, monthIndex) => (
                                        <MonthRangeButton
                                            key={`${month.number}-${month.yearOffset}`}
                                            month={month}
                                            menuYear={menuYear}
                                            selectedDate={selectedMonth}
                                            maxDate={maxDate}
                                            minDate={minDate}
                                            monthIndex={monthIndex}
                                            onMonthClick={handleMonthClick}
                                        />
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </section>
    );
};

export { MonthRangeCalendar };
